// Imports ✨
import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'
import collapse from '@alpinejs/collapse'
import lazySizes from 'lazysizes'
import gsap from 'gsap'
import { SplitText } from './SplitText'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Swup from 'swup'
import SwupScrollPlugin from '@swup/scroll-plugin'
import SwupScriptsPlugin from '@swup/scripts-plugin'
import EmblaCarousel from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import marquee3k from 'marquee3000'
import { initCollection } from './collection.js'
import { initStoreLocator } from './map.js'
import './disableCookies.js'

function isMobile() {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth <= 768
  )
}

// Swup for page transitions
const swup = new Swup({
  plugins: [
    new SwupScrollPlugin(),
    new SwupScriptsPlugin({
      head: false, // Don't re-run scripts in <head>
      body: true, // Only re-run scripts in <body>
      optin: true, // Only run scripts with data-swup-reload-script attribute
    }),
  ],
})

// Update body class based on page type after page transitions
swup.hooks.on('content:replace', () => {
  const body = document.body
  const pageType = body.dataset.pageType

  // Remove all page type classes
  body.className = body.className
    .replace(/body--(?:builder|general|article|terms)\b/g, '')
    .trim()

  // Add new page type class only if pageType exists
  if (pageType) {
    body.classList.add(`body--${pageType}`)
  }
})

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(SplitText)

// Alpine.js Setup 🌿
window.Alpine = Alpine
Alpine.plugin(intersect)
Alpine.plugin(collapse)
Alpine.start()

// Animations on scroll 🎭
function initAnimations() {
  ScrollTrigger.batch('.f-up', {
    onEnter: (elements) => {
      gsap.to(elements, {
        opacity: 1,
        y: 0,
        stagger: 0.1,
        duration: 0.6,
        ease: 'power2.out',
      })
    },
    start: 'top 90%',
    once: true,
  })

  // Animate items already in viewport on page load (excluding carousel slides)
  gsap.to('.f-up:not(.embla__slide)', {
    opacity: 1,
    y: 0,
    stagger: 0.1,
    duration: 0.6,
    ease: 'power2.out',
    delay: 0.2,
  })

  ScrollTrigger.batch('.f-down', {
    onEnter: (elements) => {
      gsap.to(elements, { opacity: 1, y: 0, stagger: 0.15 })
    },
  })

  // Only animate forms if they exist
  const forms = document.querySelectorAll('form')
  if (forms.length > 0) {
    gsap.to(forms, {
      opacity: 1,
      y: 0,
      stagger: 0.15,
      duration: 0.5,
      ease: 'power2.out',
    })
  }

  if (document.querySelector('.banner__button')) {
    gsap.to('.banner__button', {
      y: 0,
      duration: 0.5,
      ease: 'power2.out',
    })
  }
}

// Set Active Nav Link 🔗
const setActiveNav = () => {
  const nav = document.querySelector('#nav')
  if (!nav) return

  const currentSlug = window.location.pathname.replace(/\/$/, '') || '/'

  nav.querySelectorAll('.nav__link a').forEach((link) => {
    const href = link.getAttribute('href')
    if (!href) return

    const linkSlug =
      new URL(href, window.location.origin).pathname.replace(/\/$/, '') || '/'

    link.classList.toggle('active', linkSlug === currentSlug)
  })
}

// Home page class toggle 🏡
function checkHomePage() {
  const isHome =
    window.location.pathname === '/' ||
    window.location.href === window.location.origin + '/'
  document.body.classList.toggle('body--home', isHome)
}

// Banner logic 🌊
function checkBanner() {
  const banner = document.querySelector('.banner')
  const nav = document.querySelector('.nav')
  nav.classList.toggle('nav--no-banner', !banner)
}

// Replace <br> in small viewports 🪜
function removeBr(selector) {
  if (window.innerWidth <= 768) {
    const el = document.querySelector(selector)
    if (el) el.innerHTML = el.innerHTML.replace(/<br\s*\/?>/gi, ' ')
  }
}

function initEmbla() {
  const viewports = document.querySelectorAll('.embla__viewport')
  if (!viewports.length) return

  viewports.forEach((viewport, index) => {
    // Check if the parent .embla has the modifier class embla--featured, embla--images, or embla--Guide
    const emblaContainer = viewport.closest('.embla')
    const isFeatured =
      emblaContainer && emblaContainer.classList.contains('embla--featured')
    const isImages =
      emblaContainer && emblaContainer.classList.contains('embla--images')
    const isGuide =
      emblaContainer && emblaContainer.classList.contains('embla--Guide')

    const embla = EmblaCarousel(
      viewport,
      {
        align: isFeatured ? 'center' : 'start',
        dragFree: isFeatured ? false : true,
        loop: !isGuide, // Disable loop for Guide carousels
      },
      [
        Autoplay({
          delay: 6000,
          playOnInit: true,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
        }),
      ]
    )

    // Get prev/next buttons
    const prevBtn = emblaContainer.querySelector('.embla__button--prev, .embla__prev')
    const nextBtn = emblaContainer.querySelector('.embla__button--next, .embla__next')

    // Add click handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        embla.scrollPrev()
      })
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        embla.scrollNext()
      })
    }

    // Pause/play toggle for autoplay (a11y - WCAG 2.2.2)
    const pauseBtn = emblaContainer.querySelector('.embla__button--pause')
    if (pauseBtn) {
      const autoplayPlugin = embla.plugins().autoplay
      pauseBtn.addEventListener('click', () => {
        const isPlaying = pauseBtn.getAttribute('data-playing') === 'true'
        if (isPlaying && autoplayPlugin) {
          autoplayPlugin.stop()
          pauseBtn.setAttribute('data-playing', 'false')
          pauseBtn.setAttribute('aria-label', 'Play autoplay')
          pauseBtn.textContent = '▶'
        } else if (autoplayPlugin) {
          autoplayPlugin.play()
          pauseBtn.setAttribute('data-playing', 'true')
          pauseBtn.setAttribute('aria-label', 'Pause autoplay')
          pauseBtn.textContent = '⏸'
        }
      })
    }

    // Fade in carousel slides after a brief delay to ensure images are loading
    setTimeout(() => {
      const slides = emblaContainer.querySelectorAll('.embla__slide.f-up')
      gsap.to(slides, {
        opacity: 1,
        y: 0,
        stagger: 0.1,
        duration: 0.6,
        ease: 'power2.out',
      })
    }, 400)
  })
}

// Active button highlight 📂
function initActiveButton() {
  const slug = window.location.pathname.split('/').filter(Boolean).pop()
  document.querySelectorAll('#buttons a').forEach((button) => {
    const btnSlug = button.textContent
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^-\u007F]+/g, '')
    button.classList.toggle('active', btnSlug === slug)
  })
}

// Hover contrast helper 🔍
function adjustButtonHover() {
  document.querySelectorAll('#buttons a').forEach((button) => {
    const [r, g, b] = window
      .getComputedStyle(button)
      .color.match(/\d+/g)
      .map(Number)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    button.classList.add(brightness > 128 ? 'light-hover' : 'dark-hover')
  })
}

// ==========================================================================
// 🛠️ Fix admin bar url
// ==========================================================================

function updateAdminBarEditLink() {
  const editLink = document.querySelector('.admin-bar__link--highlight')
  if (!editLink) return

  // Get current pathname and clean it up
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '')

  // Handle home page separately if needed
  const panelSlug = path === '' ? 'home' : path.replace(/\//g, '+')

  const panelOrigin = window.location.origin.replace(/\/$/, '')
  editLink.href = `${panelOrigin}/panel/pages/${panelSlug}`
}

if (document.querySelector('.admin-bar')) {
  document.body.classList.add('has-admin-bar')
}

function initHeroFloatingButton() {
  const hero = document.querySelector('.hero--has-link')
  if (!hero) return
  const nav = document.querySelector('.nav')
  const btnInHero = hero.querySelector('.hero__floating-btn')
  if (!btnInHero) return

  const ph = document.createComment('btn-placeholder')
  btnInHero.parentNode.insertBefore(ph, btnInHero)
  document.body.appendChild(btnInHero)
  const floatingBtn = btnInHero

  let lastX = 0,
    lastY = 0
  let scrolling = false
  let graceActive = false
  let scrollTimer, graceTimer, reenableTimer

  const HIDE_GRACE = 100
  const SCROLL_STOP = 200
  const REENABLE_DELAY = 250

  const inHero = (x, y) => {
    const r = hero.getBoundingClientRect()
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
  }
  const nearNav = (x, y) => {
    if (!nav) return false
    const r = nav.getBoundingClientRect()
    const b = 50

    // Check if menu is open
    const menuOpen = nav.classList.contains('nav--open')
    if (menuOpen) return true

    return (
      x >= r.left - b && x <= r.right + b && y >= r.top - b && y <= r.bottom + b
    )
  }
  const heroInView = () => {
    const r = hero.getBoundingClientRect()
    return r.bottom > 0 && r.top < window.innerHeight
  }
  const showable = (x, y) =>
    !scrolling && heroInView() && inHero(x, y) && !nearNav(x, y)

  const place = (x = lastX, y = lastY) => {
    lastX = x
    lastY = y
    floatingBtn.style.left = x + 'px'
    floatingBtn.style.top = y + 'px'
    floatingBtn.style.opacity = showable(x, y) ? '1' : '0'
  }

  const onScrollStart = () => {
    clearTimeout(scrollTimer)
    clearTimeout(reenableTimer)
    if (!graceActive && !scrolling) {
      graceActive = true
      graceTimer = setTimeout(() => {
        scrolling = true
        floatingBtn.style.opacity = '0'
        graceActive = false
      }, HIDE_GRACE)
    }
    scrollTimer = setTimeout(() => {
      clearTimeout(graceTimer)
      graceActive = false
      reenableTimer = setTimeout(() => {
        scrolling = false
        place()
      }, REENABLE_DELAY)
    }, SCROLL_STOP)
  }

  document.addEventListener(
    'pointermove',
    (e) => {
      if (!scrolling) place(e.clientX, e.clientY)
      else {
        lastX = e.clientX
        lastY = e.clientY
      }
    },
    { passive: true }
  )

  hero.addEventListener('pointerenter', (e) => place(e.clientX, e.clientY), {
    passive: true,
  })
  hero.addEventListener(
    'pointerleave',
    () => {
      floatingBtn.style.opacity = '0'
    },
    { passive: true }
  )

  window.addEventListener('scroll', onScrollStart, { passive: true })
  window.addEventListener('wheel', onScrollStart, { passive: true })
  window.addEventListener('touchmove', onScrollStart, { passive: true })
  window.addEventListener('resize', () => place(), { passive: true })
}

// IMPROVED CLEANUP FUNCTION
function cleanupScrollTriggers() {
  // Kill all existing ScrollTriggers
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill())

  // Clear any lingering GSAP properties on stacker elements
  document
    .querySelectorAll('.stacker__image img, .stacker__image figcaption')
    .forEach((el) => {
      gsap.set(el, { clearProps: 'all' })
    })

  // Refresh ScrollTrigger after a brief delay to recalculate positions
  ScrollTrigger.refresh()
}
// Map functionality handled in map.js

// UPDATED INIT FUNCTION
function init() {
  // Clean up existing ScrollTriggers before creating new ones
  cleanupScrollTriggers()
  lazySizes.init()
  checkHomePage()
  checkBanner()
  removeBr('h1')
  initAnimations()
  initEmbla()
  adjustButtonHover()
  initActiveButton()
  updateAdminBarEditLink()
  initCollection()
  initStoreLocator()
  initOpeningHoursPopup()
  initCheckoutModal()
  // initBurger();

  document
    .querySelectorAll('img.lazyloaded')
    .forEach((img) => img.classList.remove('blur-up'))

  if (document.querySelector('nav')) setActiveNav()

  // Initialize marquee ticker
  if (document.querySelector('.marquee3k')) {
    marquee3k.init()
  }

  // Initialize event overlay
  initEventOverlay()

  // Initialize ticket modal buttons
  initTicketModalButtons()

  // Initialize visit overlay
  initVisitOverlay()

  // Initialize image zoom
  initImageZoom()

  // Initialize Matterport toggle
  toggleMatterport()

  // Initialize route tooltips
  initRouteTooltips()

  // Initialize search
  initLiveSearch()
  initClearSearchButton()

  // Initialize hero floating button
  initHeroFloatingButton()

  // Initialize hero text animation
  initHeroAnimation()
}

// ==========================================================================
// Hero Animation
// ==========================================================================

function initHeroAnimation() {
  const hero = document.querySelector('.hero')
  if (!hero) return

  const topText = hero.querySelector('.hero__toptext p')
  const bottomText = hero.querySelector('.hero__bottomtext p')
  const links = hero.querySelectorAll('.hero__links li')

  if (!topText || !bottomText || !links.length) return

  // Split both text elements into words
  const splitTop = new SplitText(topText, { type: 'words' })
  const splitBottom = new SplitText(bottomText, { type: 'words' })

  // Set initial states
  gsap.set(splitTop.words, { opacity: 0, y: 10 })
  gsap.set(splitBottom.words, { opacity: 0, y: 10 })
  gsap.set(links, { opacity: 0, y: 10 })

  // Create timeline with initial delay
  const tl = gsap.timeline({
    defaults: { ease: 'power3.out' },
    delay: .75
  })

  // 1. Animate top text words first
  tl.to(splitTop.words, {
    opacity: 1,
    y: 0,
    duration: 1,
    stagger: 0.2
  })

  // 2. Then animate bottom text words (with more delay between sections)
  .to(splitBottom.words, {
    opacity: 1,
    y: 0,
    duration: 1,
    stagger: 0.2
  }, '+=0.1')

  // 3. Finally animate links (with more delay)
  .to(links, {
    opacity: 1,
    y: 0,
    duration: 0.6,
    stagger: 0.15
  }, '+=0.2')
}

// Run Init on Load 🚀
document.addEventListener('DOMContentLoaded', init)

// Swup hooks for page transitions
swup.hooks.on('page:view', () => {
  // Longer delay to ensure DOM is fully rendered and previous animations are cleared
  setTimeout(() => {
    init()

    // Reset focus to main content for keyboard/screen reader users (a11y - WCAG 2.4.3)
    const main = document.getElementById('swup')
    if (main) {
      main.setAttribute('tabindex', '-1')
      main.focus({ preventScroll: true })
    }
  }, 150)
})

// More thorough cleanup when leaving page
swup.hooks.on('visit:start', () => {
  cleanupScrollTriggers()
  gsap.killTweensOf('*')
})

// ==========================================================================
// Overlay Functions
// ==========================================================================

function openEventOverlay(eventUrl) {
  const overlay = document.querySelector('.overlay')
  const overlayContent = document.querySelector('.overlay__content')

  if (!overlay || !overlayContent) return

  // Show loading state
  overlayContent.innerHTML = '<div class="overlay__loading">Loading...</div>'
  overlay.classList.add('overlay--active')

  // Prevent body scroll and preserve scroll position
  const scrollY = window.scrollY
  document.body.style.top = `-${scrollY}px`
  document.body.classList.add('body--fixed')

  // Fetch event data
  fetch(`${eventUrl}?overlay=true`)
    .then((response) => response.json())
    .then((eventData) => {
      // Create close button
      const closeButton = document.createElement('button')
      closeButton.className = 'overlay__close'
      closeButton.innerHTML = ''
      closeButton.setAttribute('aria-label', 'Close overlay')

      // Create overlay content with proper BEM structure
      const overlayBody = document.createElement('div')

      overlayBody.innerHTML = `
        ${eventData.imageUrl ? `<div class="overlay__image"><img src="${eventData.imageUrl}" alt="${eventData.imageAlt}" /></div>` : ''}
        <div class="overlay__inner">
          ${
            eventData.tags && eventData.tags.length
              ? `
            <ul class="overlay__tags">
              ${eventData.tags.map((tag) => `<li>${tag}</li>`).join('')}
            </ul>
          `
              : ''
          }
          <h2 class="overlay__title">
            ${eventData.title}</br>
            ${eventData.date ? `<span>${eventData.date}</span>` : ''}
          </h2>
          ${
            eventData.price ||
            eventData.date ||
            eventData.time ||
            eventData.location ||
            eventData.room
              ? `
            <div class="overlay__meta">
              ${eventData.price ? `<div class="overlay__price">${eventData.price}</div>` : ''}
              ${eventData.date ? `<div class="overlay__date">${eventData.date}</div>` : ''}
              ${eventData.time ? `<div class="overlay__time">${eventData.time}</div>` : ''}
              ${eventData.location ? `<div class="overlay__location">${eventData.location}</div>` : ''}
              ${eventData.room ? `<div class="overlay__room">${eventData.room}</div>` : ''}
            </div>
          `
              : ''
          }
          ${eventData.description ? `<div class="overlay__body">${eventData.description}</div>` : ''}
          <div class="overlay__actions">
            ${eventData.buttonHtml || ''}
          </div>
        </div>
      `

      // Clear loading and add content
      overlayContent.innerHTML = ''
      overlayContent.appendChild(closeButton)
      overlayContent.appendChild(overlayBody)

      // Attach modal ticket handlers for buttons in overlay
      const ticketButtons = overlayBody.querySelectorAll('.btn--ticket-modal')
      ticketButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault()
          const embedCode = btn.getAttribute('data-ticket-embed')
          if (embedCode) {
            openTicketModal(embedCode)
          }
        })
      })

      // Add close functionality
      closeButton.addEventListener('click', closeEventOverlay)

      // Close on click anywhere outside overlay content
      const handleClickOutside = (e) => {
        if (
          overlay.classList.contains('overlay--active') &&
          !overlayContent.contains(e.target)
        ) {
          closeEventOverlay()
        }
      }
      document.addEventListener('click', handleClickOutside)

      // Close on escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeEventOverlay()
        }
      }
      document.addEventListener('keydown', handleEscape)

      // Store references for cleanup
      overlay._handleClickOutside = handleClickOutside
      overlay._handleEscape = handleEscape
    })
    .catch((error) => {
      overlayContent.innerHTML =
        '<div class="overlay__error">Error loading event details.</div>'
    })
}

function closeEventOverlay() {
  const overlay = document.querySelector('.overlay')
  const overlayContent = document.querySelector('.overlay__content')

  if (!overlay) return

  // Hide overlay
  overlay.classList.remove('overlay--active')

  // Clear content after animation
  setTimeout(() => {
    if (overlayContent) {
      overlayContent.innerHTML = ''
    }
  }, 300) // Match CSS transition duration

  // Clean up event listeners
  if (overlay._handleEscape) {
    document.removeEventListener('keydown', overlay._handleEscape)
    overlay._handleEscape = null
  }
  if (overlay._handleClickOutside) {
    document.removeEventListener('click', overlay._handleClickOutside)
    overlay._handleClickOutside = null
  }

  // Restore body scroll and position
  document.body.classList.remove('body--fixed')
  const scrollY = document.body.style.top
  document.body.style.top = ''
  window.scrollTo(0, parseInt(scrollY || '0') * -1)
}

// Ticket Tailor Modal Functions
function openTicketModal(embedCode) {
  // Create modal container if it doesn't exist
  let modal = document.getElementById('ticket-modal')
  if (!modal) {
    modal = document.createElement('div')
    modal.id = 'ticket-modal'
    modal.className = 'ticket-modal'
    modal.innerHTML = `
      <div class="ticket-modal__overlay"></div>
      <div class="ticket-modal__content">
        <button class="ticket-modal__close" aria-label="Close ticket widget">×</button>
        <div class="ticket-modal__widget"></div>
      </div>
    `
    document.body.appendChild(modal)

    // Add close handlers
    modal
      .querySelector('.ticket-modal__close')
      .addEventListener('click', closeTicketModal)
    modal
      .querySelector('.ticket-modal__overlay')
      .addEventListener('click', closeTicketModal)

    // Close when clicking outside the content
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeTicketModal()
      }
    })

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'Escape' &&
        modal.classList.contains('ticket-modal--active')
      ) {
        closeTicketModal()
      }
    })
  }

  // Clear previous widget
  const widgetContainer = modal.querySelector('.ticket-modal__widget')
  widgetContainer.innerHTML = ''

  // Insert the embed code directly
  widgetContainer.innerHTML = embedCode

  // Execute the script tag
  const scripts = widgetContainer.querySelectorAll('script')
  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script')
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value)
    })
    if (oldScript.src) {
      newScript.src = oldScript.src
    }
    if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent
    }
    oldScript.parentNode.replaceChild(newScript, oldScript)
  })

  // Show modal
  modal.classList.add('ticket-modal--active')
  document.body.style.overflow = 'hidden'

  // Listen for iframe resize messages from Ticket Tailor
  if (!window.ticketTailorResizeListener) {
    window.ticketTailorResizeListener = true
    window.addEventListener('message', function (event) {
      // Check if message is from Ticket Tailor (try different message formats)
      if (event.data && typeof event.data === 'object') {
        const iframe = document.querySelector('.ticket-modal iframe')
        if (iframe) {
          // Try different height properties
          if (event.data.type === 'tt-widget-resize' && event.data.height) {
            iframe.style.height = event.data.height + 'px'
          } else if (event.data.height) {
            iframe.style.height = event.data.height + 'px'
          } else if (event.data.frameHeight) {
            iframe.style.height = event.data.frameHeight + 'px'
          }
        }
      }
    })
  }

  // Set up iframe height monitoring
  const checkIframeHeight = () => {
    const iframe = widgetContainer.querySelector('iframe')
    if (iframe) {
      // Send message to iframe to get its content height
      try {
        iframe.contentWindow.postMessage({ type: 'getHeight' }, '*')
      } catch (e) {
        // Cross-origin restriction
      }

      // Also check if iframe has a data-height attribute that TT might set
      const dataHeight = iframe.getAttribute('data-height')
      if (dataHeight) {
        iframe.style.height = dataHeight + 'px'
      }

      // Monitor the iframe's scrollHeight via ResizeObserver
      if (window.ResizeObserver && !iframe._resizeObserver) {
        iframe._resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            // This might not work for cross-origin, but worth trying
            console.log('Iframe resized:', entry.contentRect.height)
          }
        })
        iframe._resizeObserver.observe(iframe)
      }
    }
  }

  // Watch for iframe to be added using MutationObserver
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (
          node.tagName === 'IFRAME' ||
          (node.querySelector && node.querySelector('iframe'))
        ) {
          checkIframeHeight()

          // Also set up onload handler
          const iframe = widgetContainer.querySelector('iframe')
          if (iframe && !iframe._heightChecked) {
            iframe._heightChecked = true

            // Ensure iframe is scrollable
            iframe.setAttribute('scrolling', 'yes')
            iframe.style.overflow = 'auto'

            iframe.onload = function () {
              // Check height after load
              setTimeout(checkIframeHeight, 100)
              // And keep checking periodically as content changes
              const heightInterval = setInterval(() => {
                if (!modal.classList.contains('ticket-modal--active')) {
                  clearInterval(heightInterval)
                  return
                }
                checkIframeHeight()
              }, 1000)
            }
          }
        }
      })
    })
  })

  observer.observe(widgetContainer, { childList: true, subtree: true })

  // Initial check after a delay
  setTimeout(checkIframeHeight, 500)
  setTimeout(checkIframeHeight, 1500)
}

function closeTicketModal() {
  const modal = document.getElementById('ticket-modal')
  if (modal) {
    modal.classList.remove('ticket-modal--active')
    document.body.style.overflow = ''

    // Clean up widget after animation
    setTimeout(() => {
      const widgetContainer = modal.querySelector('.ticket-modal__widget')
      if (widgetContainer) {
        widgetContainer.innerHTML = ''
      }
    }, 300)
  }
}

// Initialize ticket modal buttons
function initTicketModalButtons() {
  document.querySelectorAll('.btn--ticket-modal').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      const embedCode = btn.getAttribute('data-ticket-embed')
      if (embedCode) {
        openTicketModal(embedCode)
      }
    })
  })
}

// Initialize overlay functionality
function initEventOverlay() {
  document.querySelectorAll('.event-overlay-trigger').forEach((trigger) => {
    // Remove existing event listeners
    const newTrigger = trigger.cloneNode(true)
    trigger.parentNode.replaceChild(newTrigger, trigger)

    newTrigger.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const eventUrl =
        newTrigger.getAttribute('data-event-url') ||
        newTrigger.getAttribute('href')
      openEventOverlay(eventUrl)
    })
  })
}

// ==========================================================================
// Visit Location Overlay
// ==========================================================================

function openVisitOverlay(visitUrl) {
  const overlay = document.querySelector('.overlay')
  const overlayContent = document.querySelector('.overlay__content')

  if (!overlay || !overlayContent) return

  // Show loading state
  overlayContent.innerHTML = '<div class="overlay__loading">Loading...</div>'
  overlay.classList.add('overlay--active')

  // Prevent body scroll and preserve scroll position
  const scrollY = window.scrollY
  document.body.style.top = `-${scrollY}px`
  document.body.classList.add('body--fixed')

  // Fetch visit location data
  fetch(`${visitUrl}?overlay=true`)
    .then((response) => response.json())
    .then((visitData) => {
      // Create close button
      const closeButton = document.createElement('button')
      closeButton.className = 'overlay__close'
      closeButton.innerHTML = ''
      closeButton.setAttribute('aria-label', 'Close overlay')

      // Create overlay content with proper BEM structure
      const overlayBody = document.createElement('div')

      overlayBody.innerHTML = `
        ${visitData.imageUrl ? `<div class="overlay__image"><img src="${visitData.imageUrl}" alt="${visitData.imageAlt}" /></div>` : ''}
        <div class="overlay__inner">
          ${
            visitData.tags && visitData.tags.length
              ? `
            <ul class="overlay__tags">
              ${visitData.tags.map((tag) => `<li>${tag}</li>`).join('')}
            </ul>
          `
              : ''
          }
          <h2 class="overlay__title">
            ${visitData.title}
            ${visitData.trailType ? `<br><span class="overlay__trail-type">${visitData.trailType}</span>` : ''}
            ${visitData.episode ? `<br><span class="overlay__episode">Episode: ${visitData.episode}</span>` : ''}
          </h2>
          ${
            visitData.categories && visitData.categories.length
              ? `
            <div class="overlay__categories">
              ${visitData.categories.map((cat) => `<span class="overlay__category-tag">${cat}</span>`).join('')}
            </div>
          `
              : ''
          }
          ${visitData.description ? `<div class="overlay__body">${visitData.description}</div>` : ''}
          ${
            visitData.buttonsHtml
              ? `
            <div class="overlay__actions">
              ${visitData.buttonsHtml}
            </div>
          `
              : ''
          }
        </div>
      `

      // Clear loading and add content
      overlayContent.innerHTML = ''
      overlayContent.appendChild(closeButton)
      overlayContent.appendChild(overlayBody)

      // Add close functionality
      closeButton.addEventListener('click', closeVisitOverlay)

      // Close on click anywhere outside overlay content
      const handleClickOutside = (e) => {
        if (
          overlay.classList.contains('overlay--active') &&
          !overlayContent.contains(e.target)
        ) {
          closeVisitOverlay()
        }
      }
      document.addEventListener('click', handleClickOutside)

      // Close on escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeVisitOverlay()
        }
      }
      document.addEventListener('keydown', handleEscape)

      // Store references for cleanup
      overlay._handleClickOutside = handleClickOutside
      overlay._handleEscape = handleEscape
    })
    .catch((error) => {
      console.error('Error loading visit location:', error)
      overlayContent.innerHTML =
        '<div class="overlay__error">Failed to load location details.</div>'
    })
}

function closeVisitOverlay() {
  const overlay = document.querySelector('.overlay')
  const overlayContent = document.querySelector('.overlay__content')

  if (!overlay) return

  // Hide overlay
  overlay.classList.remove('overlay--active')

  // Clear content after animation
  setTimeout(() => {
    if (overlayContent) {
      overlayContent.innerHTML = ''
    }
  }, 300)

  // Clean up event listeners
  if (overlay._handleEscape) {
    document.removeEventListener('keydown', overlay._handleEscape)
    overlay._handleEscape = null
  }
  if (overlay._handleClickOutside) {
    document.removeEventListener('click', overlay._handleClickOutside)
    overlay._handleClickOutside = null
  }

  // Restore body scroll and position
  document.body.classList.remove('body--fixed')
  const scrollY = document.body.style.top
  document.body.style.top = ''
  window.scrollTo(0, parseInt(scrollY || '0') * -1)
}

function initVisitOverlay() {
  document.querySelectorAll('.visit-overlay-trigger').forEach((trigger) => {
    // Remove existing event listeners
    const newTrigger = trigger.cloneNode(true)
    trigger.parentNode.replaceChild(newTrigger, trigger)

    newTrigger.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const visitUrl = newTrigger.getAttribute('href')
      openVisitOverlay(visitUrl)
    })
  })
}

// ==========================================================================
// Image Zoom Overlay
// ==========================================================================

function initImageZoom() {
  const zoomBtns = document.querySelectorAll('.collection-single__zoom-btn')
  const zoomImages = document.querySelectorAll('.collection-single__thumb')
  const imageZoom = document.getElementById('imageZoom')

  if (!imageZoom) return

  // Handle zoom button clicks
  zoomBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()

      const imageUrl = btn.getAttribute('data-image-url')
      const imageAlt = btn.getAttribute('data-image-alt')

      if (imageUrl) {
        openImageZoom(imageUrl, imageAlt)
      }
    })
  })

  // Handle clicking on the image itself
  zoomImages.forEach((thumb) => {
    thumb.addEventListener('click', (e) => {
      // Don't trigger if clicking the button
      if (e.target.closest('.collection-single__zoom-btn')) {
        return
      }

      const btn = thumb.querySelector('.collection-single__zoom-btn')
      if (btn) {
        const imageUrl = btn.getAttribute('data-image-url')
        const imageAlt = btn.getAttribute('data-image-alt')

        if (imageUrl) {
          openImageZoom(imageUrl, imageAlt)
        }
      }
    })
  })

  // Close on clicking the overlay background
  imageZoom.addEventListener('click', (e) => {
    if (e.target === imageZoom) {
      closeImageZoom()
    }
  })

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && imageZoom.classList.contains('active')) {
      closeImageZoom()
    }
  })

  function openImageZoom(imageUrl, imageAlt) {
    const content = imageZoom.querySelector('.image-zoom__content')

    // Clear previous content
    content.innerHTML = ''

    // Create close button
    const closeBtn = document.createElement('button')
    closeBtn.className = 'image-zoom__close'
    closeBtn.setAttribute('aria-label', 'Close image')
    closeBtn.addEventListener('click', closeImageZoom)

    // Create image element
    const img = document.createElement('img')
    img.src = imageUrl
    img.alt = imageAlt || ''
    img.className = 'image-zoom__image'

    // Set initial transition state
    img.style.transform = 'scale(0.95)'
    img.style.opacity = '0'
    img.style.transition = 'transform 0.4s ease, opacity 0.4s ease'

    // Append elements
    content.appendChild(closeBtn)
    content.appendChild(img)

    // Show overlay
    imageZoom.classList.add('active')

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    // Trigger animation after a brief delay
    setTimeout(() => {
      img.style.transform = 'scale(1)'
      img.style.opacity = '1'
    }, 50)
  }

  function closeImageZoom() {
    imageZoom.classList.remove('active')
    document.body.style.overflow = ''

    // Clear content after animation completes
    setTimeout(() => {
      const content = imageZoom.querySelector('.image-zoom__content')
      if (content) {
        content.innerHTML = ''
      }
    }, 400)
  }
}

// ==========================================================================
// Matterport View
// ==========================================================================

// Matterport toggle functionality
function toggleMatterport() {
  const buttons = document.querySelectorAll('.matterport-toggle')
  const matterport = document.querySelector('.matterport')

  if (!buttons.length || !matterport) return

  // Add click handler to all toggle buttons
  buttons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault()

      // Check if matterport is already open
      if (matterport.classList.contains('active')) {
        closeMatterport()
      } else {
        openMatterport()
      }
    })
  })

  // Close on clicking the overlay background
  matterport.addEventListener('click', (e) => {
    if (e.target === matterport) {
      closeMatterport()
    }
  })

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && matterport.classList.contains('active')) {
      closeMatterport()
    }
  })

  function openMatterport() {
    // Toggle active class on all buttons
    buttons.forEach((btn) => btn.classList.add('active'))

    // Show matterport overlay
    matterport.classList.add('active')

    // Prevent body scroll
    document.body.style.overflow = 'hidden'

    // Dynamically load iframe on first click
    if (!matterport.querySelector('iframe')) {
      const matterportUrl = matterport.dataset.src
      if (matterportUrl) {
        const iframe = document.createElement('iframe')
        iframe.src = matterportUrl
        iframe.style.width = '90vw'
        iframe.style.height = '90vh'
        iframe.style.border = 'none'
        iframe.allowFullscreen = true
        iframe.allow = 'xr-spatial-tracking; vr'

        // Set initial transition state
        iframe.style.transform = 'translateY(20px)'
        iframe.style.opacity = '0'
        iframe.style.transition = 'transform 0.4s ease, opacity 0.4s ease'
        iframe.style.borderRadius = '8px'
        iframe.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.3)'

        matterport.appendChild(iframe)

        // Trigger animation after a brief delay
        setTimeout(() => {
          iframe.style.transform = 'translateY(0)'
          iframe.style.opacity = '1'
        }, 100)
      }
    }
  }

  function closeMatterport() {
    // Remove active class from all buttons
    buttons.forEach((btn) => btn.classList.remove('active'))

    matterport.classList.remove('active')
    document.body.style.overflow = ''
  }
}

// ==========================================================================
// Search Functionality
// ==========================================================================

function renderRelatedKeywords(tags) {
  const container = document.getElementById('related-tags')
  if (!container) return

  if (tags.length) {
    const tagsHTML = tags
      .map(
        (tag) =>
          `<a href="#" class="search__tag" data-keyword="${tag}">${tag}</a>`
      )
      .join('')

    container.innerHTML = `<div class="search__tags">${tagsHTML}</div>`
    setupTagClickListeners()
  } else {
    container.innerHTML = ''
  }
}

function setupTagClickListeners() {
  document.querySelectorAll('.search__tag').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault()
      const keyword = link.getAttribute('data-keyword')
      const input = document.getElementById('live-search-input')
      if (!input) return
      input.value = keyword
      input.dispatchEvent(new Event('keyup'))
    })
  })
}

function initLiveSearch() {
  const input = document.getElementById('live-search-input')
  const clearBtn = document.querySelector('.search__clear-btn')
  const resultsList = document.getElementById('live-search-results')
  const relatedTags = document.getElementById('related-tags')
  const featuredItems = document.getElementById('featured-items')

  if (!input || !resultsList) return

  let debounceTimer

  const performSearch = () => {
    const query = input.value.trim()

    if (query.length < 2) {
      resultsList.innerHTML = ''
      if (relatedTags) relatedTags.innerHTML = ''
      if (clearBtn) clearBtn.classList.remove('active')
      if (featuredItems) featuredItems.style.display = 'block'
      return
    }

    // Hide featured items when searching
    if (featuredItems) featuredItems.style.display = 'none'

    // Add loading state
    resultsList.classList.add('loading')

    fetch(`/search.json?q=${encodeURIComponent(query)}`)
      .then((res) => res.json())
      .then((data) => {
        const results = data.results || []
        const tags = data.relatedTags || []

        // Update search status for screen readers (a11y - WCAG 4.1.3)
        const searchStatus = document.getElementById('search-status')

        if (results.length === 0) {
          resultsList.innerHTML =
            '<div class="search__no-results">No results found.</div>'
          if (relatedTags) relatedTags.innerHTML = ''
          if (searchStatus) searchStatus.textContent = `No results found for "${query}".`
          resultsList.classList.remove('loading')
          return
        }

        if (searchStatus) searchStatus.textContent = `${results.length} result${results.length !== 1 ? 's' : ''} found for "${query}".`

        // Build grid HTML
        let html = ''
        results.forEach((item) => {
          html += `
            <li class="col col--sm-12 col--md-3 collection-item f-up search-result-item">
              <a href="${item.url}" class="collection-item__link">
                ${
                  item.thumb
                    ? `
                  <figure class="collection-item__image">
                    <img class="lazyload"
                         src="${item.thumb.placeholder}"
                         data-src="${item.thumb.url}"
                         data-srcset="${item.thumb.srcset}"
                         width="${item.thumb.width}"
                         height="${item.thumb.height}"
                         alt="${item.title}">
                  </figure>
                `
                    : `
                  <div class="collection-item__placeholder">No Image</div>
                `
                }
                <div class="collection-item__content">
                  <h3 class="collection-item__title">
                    ${item.title}
                  </h3>
                  ${
                    item.tags && item.tags.length
                      ? `
                    <div class="collection-item__tags">
                      ${item.tags
                        .map(
                          (tag) => `
                        <span class="collection-item__tag">
                          ${tag.text}
                        </span>
                      `
                        )
                        .join('')}
                    </div>
                  `
                      : ''
                  }
                </div>
              </a>
            </li>
          `
        })

        resultsList.innerHTML = html
        resultsList.classList.remove('loading')

        // Animate search results in
        const searchItems = resultsList.querySelectorAll('.search-result-item')
        gsap.fromTo(
          searchItems,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, stagger: 0.05, duration: 0.4, ease: 'power2.out' }
        )

        // Reinitialize lazy loading for new images
        if (typeof lazySizes !== 'undefined') {
          lazySizes.init()
        }

        // Render related tags
        renderRelatedKeywords(tags)

        if (clearBtn) clearBtn.classList.add('active')
      })
      .catch((error) => {
        resultsList.innerHTML =
          '<div class="search__no-results">An error occurred. Please try again.</div>'
        resultsList.classList.remove('loading')
      })
  }

  // Search on Enter
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      performSearch()
    }
  })

  // Debounced live search
  input.addEventListener('keyup', () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      performSearch()
    }, 200)
  })

  // Handle ?q= query parameter in URL
  const urlParams = new URLSearchParams(window.location.search)
  const initialQuery = urlParams.get('q')
  if (initialQuery && initialQuery.length >= 2) {
    input.value = initialQuery
    if (clearBtn) clearBtn.classList.add('active')
    performSearch()
  }
}

function initClearSearchButton() {
  const input = document.getElementById('live-search-input')
  const clearBtn = document.querySelector('.search__clear-btn')

  if (!input || !clearBtn) return

  clearBtn.addEventListener('click', () => {
    input.value = ''
    input.focus()
    clearBtn.classList.remove('active')

    const resultsList = document.getElementById('live-search-results')
    if (resultsList) resultsList.innerHTML = ''

    const relatedTags = document.getElementById('related-tags')
    if (relatedTags) relatedTags.innerHTML = ''

    const featuredItems = document.getElementById('featured-items')
    if (featuredItems) featuredItems.style.display = 'block'
  })

  input.addEventListener('input', () => {
    if (input.value.length > 0) {
      clearBtn.classList.add('active')
    } else {
      clearBtn.classList.remove('active')
    }
  })
}

// ==========================================================================
// Route tooltip functionality
// ==========================================================================

function initRouteTooltips() {
  document.querySelectorAll('.btn--route').forEach((button) => {
    // Remove existing event listeners
    const newBtn = button.cloneNode(true)
    button.parentNode.replaceChild(newBtn, button)

    newBtn.addEventListener('click', (e) => {
      e.preventDefault()

      const routeItem = newBtn.closest('.visit__route')
      if (!routeItem) return

      const tooltip = routeItem.querySelector('.visit__tooltip')
      if (!tooltip) return

      const isVisible = tooltip.classList.contains('is-visible')

      // Hide all other tooltips
      document.querySelectorAll('.visit__tooltip').forEach((tip) => {
        tip.classList.remove('is-visible')
      })

      // Remove active class from all buttons
      document.querySelectorAll('.btn--route').forEach((btn) => {
        btn.classList.remove('btn--route--active')
      })

      // Toggle current tooltip
      if (!isVisible) {
        tooltip.classList.add('is-visible')
        newBtn.classList.add('btn--route--active')
      }
    })
  })

  // Close tooltips when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.visit__route')) {
      document.querySelectorAll('.visit__tooltip').forEach((tip) => {
        tip.classList.remove('is-visible')
      })
      document.querySelectorAll('.btn--route').forEach((btn) => {
        btn.classList.remove('btn--route--active')
      })
    }
  })
}

// ==========================================================================
// Opening Hours Pop-up
// ==========================================================================

// Membership Checkout Modal
let checkoutFormHandler = null

function initCheckoutModal() {
  // console.log('🛒 Initializing checkout modal...')

  const modal = document.getElementById('checkout-modal')
  const form = document.getElementById('checkout-form')
  const errorDiv = document.getElementById('checkout-error')
  const checkoutButtons = document.querySelectorAll('.js-open-checkout')

  // console.log('Modal:', modal)
  // console.log('Form:', form)
  // console.log('Checkout buttons found:', checkoutButtons.length)

  // if (!modal || !form) {
  //   console.log('❌ Modal or form not found')
  //   return
  // }

  // Open modal
  checkoutButtons.forEach((btn) => {
    btn.addEventListener('click', function (e) {
      e.preventDefault()
      // console.log('✅ Checkout button clicked!')

      const priceId = this.dataset.price
      const title = this.dataset.title
      const amount = this.dataset.amount
      const interval = this.dataset.interval
      const description = this.dataset.description

      // console.log('Price ID:', priceId)

      document.getElementById('modal-title').textContent = title
      document.getElementById('modal-price').textContent =
        `€${amount} per ${interval}`
      document.getElementById('modal-description').textContent = description
      document.getElementById('price-id').value = priceId

      modal.style.display = 'block'
      // console.log('📖 Modal opened')
    })
  })

  // Close modal
  document.querySelectorAll('.js-close-modal').forEach((btn) => {
    btn.addEventListener('click', function (e) {
      e.preventDefault()
      modal.style.display = 'none'
      errorDiv.style.display = 'none'
      form.reset()
      console.log('📕 Modal closed')
    })
  })

  // Remove old form handler
  if (checkoutFormHandler) {
    form.removeEventListener('submit', checkoutFormHandler)
  }

  // Create new handler
  checkoutFormHandler = function (e) {
    e.preventDefault()
    console.log('📤 Form submitted')

    const formData = new FormData(form)

    fetch('/membership/checkout', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Response:', data)
        if (data.error) {
          errorDiv.textContent = data.error
          errorDiv.style.display = 'block'
        } else if (data.url) {
          console.log('Redirecting to Stripe...')
          window.location.href = data.url
        }
      })
      .catch((error) => {
        console.error('Error:', error)
        errorDiv.textContent = 'An error occurred. Please try again.'
        errorDiv.style.display = 'block'
      })
  }

  // Add new handler
  form.addEventListener('submit', checkoutFormHandler)
}

function initOpeningHoursPopup() {
  const popup = document.querySelector('.openingHoursPopup')
  if (!popup) return

  // Don't show popup on the /visit page
  if (
    window.location.pathname === '/visit' ||
    window.location.pathname === '/visit/'
  )
    return

  // Check if popup has already been dismissed
  const dismissed = localStorage.getItem('openingHoursPopupDismissed')
  if (dismissed === 'true') return

  let isOpen = false
  let initialScrollY = 0
  const delayMs = 800
  const scrollThreshold = 100
  const closeDelay = 400 // delay before closing after link click (ms)

  const open = () => {
    if (isOpen) return
    isOpen = true
    initialScrollY = window.scrollY
    popup.classList.add('openingHoursPopup--active')
  }

  const close = () => {
    if (!isOpen) return
    isOpen = false
    popup.classList.remove('openingHoursPopup--active')
    localStorage.setItem('openingHoursPopupDismissed', 'true') // remember closed state
  }

  setTimeout(open, delayMs)

  // Fade out when scrolled too far
  window.addEventListener(
    'scroll',
    () => {
      if (!isOpen) return
      const dist = Math.abs(window.scrollY - initialScrollY)
      if (dist > scrollThreshold) close()
    },
    { passive: true }
  )

  // Close when clicking outside
  document.addEventListener(
    'click',
    (e) => {
      if (!isOpen) return
      if (!popup.contains(e.target)) close()
    },
    true
  )

  // Close when clicking link inside popup
  popup.addEventListener('click', (e) => {
    const link = e.target.closest('a')
    if (link) {
      setTimeout(() => close(), closeDelay)
    }
  })

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close()
  })
}

// // Run on load
// document.addEventListener('DOMContentLoaded', initOpeningHoursPopup);

// // Run again after Swup transitions
// document.addEventListener('swup:contentReplaced', initOpeningHoursPopup);

// ==========================================================================
// Burger Menu
// ==========================================================================

// Store burger handlers to prevent duplicates
let burgerHandlers = null

function initBurger() {
  const nav = document.getElementById('nav')
  const btn = document.getElementById('burger')
  const dropdown = document.getElementById('navDropdown')
  if (!nav || !btn || !dropdown) return

  const open = () => {
    nav.classList.add('is-open')
    document.body.classList.add('nav-open')
    btn.setAttribute('aria-expanded', 'true')

    // Trigger fade-in animation
    requestAnimationFrame(() => {
      dropdown.classList.add('is-visible')
    })
  }

  const close = () => {
    dropdown.classList.remove('is-visible')
    nav.classList.remove('is-open')
    document.body.classList.remove('nav-open')
    btn.setAttribute('aria-expanded', 'false')
  }

  // Remove old listeners if they exist
  if (burgerHandlers) {
    document.removeEventListener('click', burgerHandlers.clickOutside)
    document.removeEventListener('keydown', burgerHandlers.keydown)
  }

  // Button click handler (auto-removed when DOM replaced)
  btn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (nav.classList.contains('is-open')) close()
    else open()
  })

  // Store handlers for cleanup
  burgerHandlers = {
    clickOutside: (e) => {
      if (!nav.classList.contains('is-open')) return
      if (!nav.contains(e.target)) close()
    },
    keydown: (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close()
    },
  }

  // Add document listeners
  document.addEventListener('click', burgerHandlers.clickOutside)
  document.addEventListener('keydown', burgerHandlers.keydown)

  // Close menu on page navigation (only set up once)
  if (typeof swup !== 'undefined' && !initBurger.swupInitialized) {
    swup.hooks.on('link:click', () => {
      if (nav.classList.contains('is-open')) {
        close()
      }
    })
    initBurger.swupInitialized = true
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initBurger()
})
